import React from 'react';
import classNames from 'classnames';
import axios from 'axios';
import { object, string, bool } from 'prop-types';
import pathfinder from '@fiverr-private/pathfinder';
import { Icon, Tooltip } from '@fiverr-private/fit';
import { r_heart } from '@fiverr-private/icons';
import { useOpenBriefCreationDrawer } from '@fiverr-private/dm_brief_creation_triggers';
import { translate } from '@fiverr-private/i18n-react';
import { HEADER_NAV_SOURCE } from '../../constants';
import { useHeaderContext } from '../../modern/context/HeaderContext';
import { pathfinderQueryEnrichment } from '../../utils/pathfinderQueryEnrichment';
import { DEFAULT_CURRENCY } from './constants';
import Link from './Link';

import './style.scss';

const isBusinessProps = { isBusiness: bool };

export const UserDashboardLink = ({ className, ...props }) => (
    <Link {...props} className={className || 'nav-link'}>
        {(i18n) => i18n.t('header.dashboard')}
    </Link>
);

UserDashboardLink.propTypes = {
    className: string,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const UserOrdersLink = ({ username, className, ...props }) => {
    const path = pathfinder(
        'user_manage_orders',
        {
            user_id: username,
        },
        {
            query: {
                source: HEADER_NAV_SOURCE,
            },
        }
    );

    return (
        <Link {...props} href={path} className="nav-link">
            {(i18n) => i18n.t('header.orders')}
        </Link>
    );
};

UserOrdersLink.propTypes = {
    username: string.isRequired,
    className: string,
};

export const UserBriefsLink = ({ ...props }) => {
    const path = pathfinder('dynamic_matching_seller_listing_page', {
        tab: 'matches',
    });

    return (
        <Link {...props} href={path} className="nav-link">
            {(i18n) => i18n.t('header.briefs')}
        </Link>
    );
};

export const UserGigsLink = ({ username, ...props }) => {
    const path = pathfinder('user_manage_gigs', {
        user_id: username,
    });

    return (
        <Link {...props} href={path} className="nav-link">
            {(i18n) => i18n.t('header.gigs')}
        </Link>
    );
};

UserGigsLink.propTypes = {
    username: string.isRequired,
};

export const UserManageCatalogLink = (props) => {
    const path = pathfinder('manage_catalog_page', null, { query: { source: HEADER_NAV_SOURCE } });

    return (
        <Link {...props} href={path} className="nav-link">
            {(i18n) => i18n.t('header.manage_catalog')}
        </Link>
    );
};

export const UserAnalyticsLink = ({ username, ...props }) => {
    const path = pathfinder(
        'user_seller_analytics_dashboard',
        {
            user_id: username,
        },
        pathfinderQueryEnrichment()
    );

    return (
        <Link {...props} href={path} className="nav-link">
            {(i18n) => i18n.t('header.analytics')}
        </Link>
    );
};

UserAnalyticsLink.propTypes = {
    username: string.isRequired,
};

export const UserAnalyticsOverviewLink = ({ username, ...props }) => {
    const path = pathfinder(
        'user_seller_analytics_dashboard',
        { user_id: username },
        pathfinderQueryEnrichment({ tab: 'overview' })
    );

    return (
        <Link {...props} href={path} className="nav-link">
            {(i18n) => i18n.t('header.analytics_overview')}
        </Link>
    );
};

UserAnalyticsOverviewLink.propTypes = {
    username: string.isRequired,
};

export const UserAnalyticsGigConversionLink = ({ username, isLocked, ...props }) => {
    const path = pathfinder(
        'user_seller_analytics_dashboard',
        { user_id: username },
        pathfinderQueryEnrichment({ tab: 'conversion' })
    );

    return (
        <Link {...props} href={path} isLocked={isLocked} className="nav-link seller-analytics-link flex-between">
            {(i18n) => i18n.t('header.analytics_gig_performance')}
        </Link>
    );
};

UserAnalyticsGigConversionLink.propTypes = {
    username: string.isRequired,
    isLocked: bool,
};

export const UserAnalyticsOrdersBreakdownLink = ({ username, isLocked, ...props }) => {
    const path = pathfinder(
        'user_seller_analytics_dashboard',
        { user_id: username },
        pathfinderQueryEnrichment({ tab: 'orders' })
    );

    return (
        <Link {...props} href={path} isLocked={isLocked} className="nav-link seller-analytics-link flex-between">
            {(i18n) => i18n.t('header.analytics_attribution')}
        </Link>
    );
};

UserAnalyticsOrdersBreakdownLink.propTypes = {
    username: string.isRequired,
    isLocked: bool,
};

export const UserAnalyticsTopKeywordsLink = ({ username, isLocked, ...props }) => {
    const path = pathfinder(
        'user_seller_analytics_dashboard',
        { user_id: username },
        pathfinderQueryEnrichment({ tab: 'keywords' })
    );

    return (
        <Link {...props} href={path} isLocked={isLocked} className="nav-link seller-analytics-link flex-between">
            {(i18n) => i18n.t('header.analytics_keywords')}
        </Link>
    );
};

UserAnalyticsTopKeywordsLink.propTypes = {
    username: string.isRequired,
    isLocked: bool,
};

export const UserAnalyticsKeywordResearchLink = ({ username, isLocked, ...props }) => {
    const path = pathfinder(
        'user_seller_analytics_dashboard',
        { user_id: username },
        pathfinderQueryEnrichment({ tab: 'keyword_research' })
    );

    return (
        <Link
            {...props}
            href={path}
            isLocked={isLocked}
            className="nav-link seller-analytics-link seller-analytics-gap-8"
        >
            {(i18n) => i18n.t('header.analytics_keyword_research')}
        </Link>
    );
};

UserAnalyticsKeywordResearchLink.propTypes = {
    username: string.isRequired,
    isLocked: bool,
};

export const UserAnalyticsLoyaltyLink = ({ username, ...props }) => {
    const path = pathfinder(
        'user_seller_analytics_dashboard',
        { user_id: username },
        pathfinderQueryEnrichment({ tab: 'repeat_business' })
    );

    return (
        <Link {...props} href={path} className="nav-link">
            {(i18n) => i18n.t('header.analytics_loyalty')}
        </Link>
    );
};

UserAnalyticsLoyaltyLink.propTypes = {
    username: string.isRequired,
};

export const UserEarningsLink = (props) => {
    const path = pathfinder('earnings_page', null, { query: { source: HEADER_NAV_SOURCE } });

    return (
        <Link {...props} href={path} className="nav-link">
            {(i18n) => i18n.t('header.earnings')}
        </Link>
    );
};

export const EventsLink = (props) => (
    <Link {...props} href="https://events.fiverr.com" className="nav-link" target="_blank" rel="noopener noreferrer">
        {(i18n) => i18n.t('header.events')}
    </Link>
);

export const BlogLink = ({ labelKey = 'header.blog', ...props }) => (
    <Link {...props} href="http://blog.fiverr.com" className="nav-link" target="_blank" rel="noopener noreferrer">
        {(i18n) => i18n.t(labelKey)}
    </Link>
);

BlogLink.propTypes = {
    labelKey: string,
};

export const ForumLink = ({ labelKey = 'header.forum', ...props }) => (
    <Link
        {...props}
        href="http://community.fiverr.com/welcome"
        className="nav-link"
        target="_blank"
        rel="noopener noreferrer"
    >
        {(i18n) => i18n.t(labelKey)}
    </Link>
);

ForumLink.propTypes = {
    labelKey: string,
};

export const CommunityStandardsLink = (props) => {
    const path = pathfinder(
        'community_standards',
        {},
        {
            query: {
                source: HEADER_NAV_SOURCE,
            },
        }
    );

    return (
        <Link {...props} href={path} className="nav-link" target="_blank">
            {(i18n) => i18n.t('header.community_standards')}
        </Link>
    );
};

export const PodcastLink = (props) => (
    <Link
        {...props}
        href="http://forum.fiverr.com/fiverrcast"
        className="nav-link"
        target="_blank"
        rel="noopener noreferrer"
    >
        {(i18n) => i18n.t('header.podcast')}
    </Link>
);

export const UserCreateBriefAndMatchLink = ({ username, ...props }) => {
    const options = {
        query: {
            source: 'from_top_navigation',
            username,
        },
    };
    const path = pathfinder('create_brief_and_match_page', {}, options);

    return (
        <Link {...props} href={path} className="nav-link">
            {(i18n) => i18n.t('header.post_buyer_requests')}
        </Link>
    );
};

UserCreateBriefAndMatchLink.propTypes = {
    username: string.isRequired,
};

export const ContactsLink = ({ query, ...props }) => {
    const path = pathfinder(
        'manage_contacts',
        {},
        {
            query,
        }
    );

    return (
        <Link {...props} href={path} className="nav-link">
            {(i18n) => i18n.t('header.contacts')}
        </Link>
    );
};

ContactsLink.propTypes = {
    query: object,
};

ContactsLink.defaultProps = {
    query: {},
};

export const ProfileLink = ({ username, ...props }) => {
    const path = pathfinder('self_view_page', { username });
    const displayText = 'header.my_profile';

    return (
        <Link {...props} href={path} className="nav-link">
            {(i18n) => i18n.t(displayText)}
        </Link>
    );
};

ProfileLink.propTypes = {
    username: string.isRequired,
};

export const BuyerProfileLink = (props) => {
    const path = pathfinder(
        'my_buyer_profile',
        {},
        {
            query: {
                source: 'avatar_menu_profile',
            },
        }
    );
    const headerProfileKey = 'header.my_profile';
    return (
        <Link {...props} href={path} className="nav-link">
            {(i18n) => i18n.t(headerProfileKey)}
        </Link>
    );
};

BuyerProfileLink.propTypes = isBusinessProps;

export const ReferralLink = (props) => {
    const path = pathfinder(
        'referral_program_show',
        {},
        {
            source: 'rp_nav',
        }
    );

    return (
        <Link {...props} href={path} className="nav-link nav-link-green">
            {(i18n) => i18n.t('header.link_label.refer_a_friend')}
        </Link>
    );
};

export const UserAccountSettingsLink = ({ isBusiness = false, ...props }) => {
    const displayText = isBusiness ? 'header.business.my_settings' : 'header.account_settings';

    const path = pathfinder('account_settings_v_5');

    return (
        <Link {...props} href={path} className="nav-link">
            {(i18n) => i18n.t(displayText)}
        </Link>
    );
};

UserAccountSettingsLink.propTypes = {
    isBusiness: bool,
};

export const BillingLink = ({ isSeller, ...props }) => {
    const displayText = 'header.billing';
    const path = `${pathfinder('billing_page', null, { query: { source: HEADER_NAV_SOURCE } })}${
        isSeller ? '&seller' : ''
    }`;

    return (
        <div className="flex flex-items-center">
            <Link {...props} href={path} className="nav-link">
                {(i18n) => i18n.t(displayText)}
            </Link>
        </div>
    );
};

BillingLink.propTypes = {
    isSeller: bool,
};

export const HelpLink = ({ userType, ...props }) => {
    const path = pathfinder('help_center_homepage', null, {
        query: {
            user_type: userType,
        },
    });

    return (
        <Link {...props} href={path} target="_blank" rel="noopener noreferrer" className="nav-link">
            {(i18n) => i18n.t('header.help')}
        </Link>
    );
};

HelpLink.propTypes = {
    userType: string.isRequired,
};

export const SupportLink = (props) => {
    const path = pathfinder('support_get_help_page', {}, { query: { source: HEADER_NAV_SOURCE } });

    return (
        <Link {...props} href={path} target="_blank" className="nav-link">
            {(i18n) => i18n.t('header.support')}
        </Link>
    );
};

export const SwitchToSellerLink = ({ username, ...props }) => {
    const path = pathfinder('user_seller_dashboard_index', {
        user_id: username,
    });

    const handleClick = () => {
        axios.post(pathfinder('users_switch_header_navigation'), {
            data: {
                header_navigation_type: 'selling',
            },
        });
    };

    return (
        <Link {...props} href={path} className="nav-link nav-link-green" onClick={handleClick}>
            {(i18n) => (
                <Tooltip
                    className="header-nav-link-tooltip"
                    boxContentClassName="tooltip-box-content"
                    content={i18n.t('header.switch_to_seller.tooltip')}
                >
                    {i18n.t('header.switch_to_seller.title')}
                </Tooltip>
            )}
        </Link>
    );
};

SwitchToSellerLink.propTypes = {
    username: string.isRequired,
};

export const ListsLink = ({ className, tooltipClassName, source = 'header_navigation', showAsIcon, ...props }) => {
    const { rollouts } = useHeaderContext();
    // One Fiverr: Marketplace saved lists live on the Freelance Network page.
    const routeName = rollouts?.inMarlinsMpListsToFreelanceNetworkRollout ? 'freelance_network_page' : 'my_collections';
    const path = pathfinder(
        routeName,
        {},
        {
            query: {
                source,
            },
        }
    );

    return (
        <Link
            {...props}
            href={path}
            className={classNames('nav-link', { 'nav-link-lists-icon': showAsIcon }, className)}
        >
            {(i18n) =>
                showAsIcon ? (
                    <Tooltip className={tooltipClassName} content={i18n.t('header.lists.title')}>
                        <Icon className="icon-button" size={18}>
                            {r_heart}
                        </Icon>
                    </Tooltip>
                ) : (
                    i18n.t('header.lists.title')
                )
            }
        </Link>
    );
};

ListsLink.propTypes = {
    className: string,
    tooltipClassName: string,
    source: string,
    showAsIcon: bool,
};

export const NewLogoLink = (props) => {
    const path = pathfinder('logo_maker_brief_page_logo_maker_brief', {}, { query: { source: HEADER_NAV_SOURCE } });

    return (
        <Link {...props} href={path} className="nav-link">
            {(i18n) => i18n.t('header.new_logo')}
        </Link>
    );
};

export const ManageLogosLink = (props) => {
    const path = pathfinder(
        'logo_maker_seller_dashboard_page',
        {},
        {
            query: {
                source: HEADER_NAV_SOURCE,
            },
        }
    );

    return (
        <Link {...props} href={path} className="nav-link">
            {(i18n) => i18n.t('header.manage_logos')}
        </Link>
    );
};

export const LogoDashboardLink = (props) => {
    const path = pathfinder(
        'logo_maker_seller_dashboard_page',
        {},
        {
            query: {
                source: HEADER_NAV_SOURCE,
            },
        }
    );

    return (
        <Link {...props} href={path} className="nav-link link-logo">
            {(i18n) => i18n.t('header.link_label.logo_maker')}
        </Link>
    );
};

export const YourBriefsLink = (props) => {
    const options = {
        query: {
            source: HEADER_NAV_SOURCE,
        },
    };

    const path = pathfinder('briefs_lobby_page', {}, options);

    return (
        <Link {...props} href={path} className="nav-link">
            {(i18n) => i18n.t('header.your_briefs')}
        </Link>
    );
};

export const TeamMemberDashboardLink = ({ isUppercase, ...props }) => (
    <Link {...props} className={'nav-link'}>
        {(i18n) => i18n.t(isUppercase ? 'header.team_dashboard_upper' : 'header.team_dashboard')}
    </Link>
);

TeamMemberDashboardLink.propTypes = {
    isUppercase: bool,
};

export const TeamMemberAccountDashboardLink = ({ ...props }) => (
    <Link {...props} className={'nav-link'}>
        {(i18n) => i18n.t('header.account_dashboard')}
    </Link>
);

export const InviteTeamMembersDashboardLink = ({ ...props }) => (
    <Link {...props} className={'nav-link'}>
        {(i18n) => i18n.t('header.invite_team_members')}
    </Link>
);

export const BriefCreationLink = ({ currency, rollouts }) => {
    const { openBriefCreationDrawer } = useOpenBriefCreationDrawer();

    const handleBriefCreationClick = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const enrichedCurrency = currency
            ? {
                  name: currency?.code,
                  template: currency?.template,
                  rate: currency?.exchange_rate,
                  forceRound: !!currency?.round_amount,
                  forceRoundFromAmount: currency?.round_amount,
              }
            : DEFAULT_CURRENCY;

        openBriefCreationDrawer({
            source: HEADER_NAV_SOURCE,
            currency: enrichedCurrency,
            isInMiraAgenticBriefsRollout: rollouts?.inCrowsPythonsAgenticBriefs,
            miraSearchParams: { source: HEADER_NAV_SOURCE },
        });
    };

    return (
        <Link className="nav-link" href="#" role="button" onClick={handleBriefCreationClick}>
            {rollouts?.inCrowsPythonsAgenticBriefs
                ? translate('header.post_job_description')
                : translate('header.post_project_brief')}
        </Link>
    );
};

BriefCreationLink.propTypes = {
    currency: object.isRequired,
    rollouts: object,
};
